import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ConfigProvider, theme } from 'antd';
import { LIGHT_THEME, DARK_THEME, applyThemeVariables } from '../styles/themeTokens';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const activeTheme = useMemo(() => (isDarkMode ? DARK_THEME : LIGHT_THEME), [isDarkMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    applyThemeVariables(activeTheme);
  }, [isDarkMode, activeTheme]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const { defaultAlgorithm, darkAlgorithm } = theme;

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ConfigProvider
        theme={{
          algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
          token: {
            colorPrimary: activeTheme.primary,
            colorBgBase: activeTheme.bgBase,
            colorTextBase: activeTheme.text,
            colorTextHeading: activeTheme.textStrong,
            colorBorder: activeTheme.border,
            borderRadius: 12,
            fontFamily: 'Manrope, Segoe UI, sans-serif',
            fontSize: 13,
            fontSizeSM: 12,
            fontSizeLG: 15,
            fontSizeHeading1: 30,
            fontSizeHeading2: 24,
            fontSizeHeading3: 20,
            fontSizeHeading4: 17,
            fontSizeHeading5: 15,
          },
          components: {
            Layout: {
              bodyBg: activeTheme.bgLayout,
              headerBg: activeTheme.bgCard,
              siderBg: activeTheme.bgSidebar,
            },
            Card: {
              colorBgContainer: activeTheme.bgCard,
            },
            Menu: {
              itemBg: 'transparent',
              itemColor: activeTheme.text,
              itemHoverColor: activeTheme.textStrong,
              itemHoverBg: activeTheme.bgSidebarElevated,
              itemSelectedBg: '#e9f3ff',
              itemSelectedColor: activeTheme.primary,
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
